import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as NGL from 'ngl';
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress, 
  Chip,
  Alert,
  IconButton,
} from '@mui/material';
import { 
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

interface ProteinViewerProps {
  proteinId: string;
  highlightCysteine?: string;
  onCysteineClick?: (cysteineId: string) => void;
}

const ProteinViewer: React.FC<ProteinViewerProps> = ({ 
  proteinId, 
  highlightCysteine,
  onCysteineClick 
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<any>(null);
  const structureRef = useRef<any>(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [structureType, setStructureType] = useState<'PDB' | 'AlphaFold' | null>(null);
  const [pdbId, setPdbId] = useState<string | null>(null);
  const [cysteineWarning, setCysteineWarning] = useState<string | null>(null);
  const [hoveredCysteine, setHoveredCysteine] = useState<string | null>(null);
  const hoverRepRef = useRef<any>(null);
  const [availableChains, setAvailableChains] = useState<string[]>([]);
  const [selectedChain, setSelectedChain] = useState<string | null>(null);

  // Suppress ResizeObserver loop error
  useEffect(() => {
    const errorHandler = (e: ErrorEvent) => {
      if (e.message && e.message.includes('ResizeObserver loop')) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };
    window.addEventListener('error', errorHandler);
    
    // Also handle unhandledrejection for Promise-based errors
    const rejectionHandler = (e: PromiseRejectionEvent) => {
      if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver loop')) {
        e.preventDefault();
        return false;
      }
    };
    window.addEventListener('unhandledrejection', rejectionHandler);
    
    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', rejectionHandler);
    };
  }, []);

  // Setup interaction handlers for hover and click
  const setupInteractionHandlers = useCallback((stage: any) => {
    // Click handler
    stage.signals.clicked.add((pickingProxy: any) => {
      if (pickingProxy && pickingProxy.atom && onCysteineClick) {
        const atom = pickingProxy.atom;
        if (atom.resname === 'CYS') {
          const cysteineId = `C${atom.resno}`;
          console.log(`Clicked on cysteine: ${cysteineId}`);
          onCysteineClick(cysteineId);
        }
      }
    });

    // Hover handler - just update cursor and state, no visual changes
    stage.signals.hovered.add((pickingProxy: any) => {
      if (pickingProxy && pickingProxy.atom) {
        const atom = pickingProxy.atom;
        if (atom.resname === 'CYS') {
          const cysteineId = `C${atom.resno}`;
          setHoveredCysteine(cysteineId);
          
          // Update mouse cursor
          if (viewerRef.current) {
            viewerRef.current.style.cursor = 'pointer';
          }
        } else {
          // Not hovering on cysteine
          setHoveredCysteine(null);
          if (viewerRef.current) {
            viewerRef.current.style.cursor = 'default';
          }
        }
      } else {
        // Not hovering over anything
        setHoveredCysteine(null);
        if (viewerRef.current) {
          viewerRef.current.style.cursor = 'default';
        }
      }
    });
  }, [onCysteineClick]);

  useEffect(() => {
    let mounted = true;

    const loadStructure = async () => {
      // Reset state
      setIsLoading(true);
      setError(null);
      setStructureType(null);
      setPdbId(null);
      setCysteineWarning(null);

      // Clean up previous viewer
      if (stageRef.current) {
        try {
          stageRef.current.dispose();
        } catch (e) {
          console.error('Error disposing stage:', e);
        }
        stageRef.current = null;
      }

      // Wait for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!viewerRef.current || !mounted) {
        console.error('Viewer ref not ready or component unmounted');
        return;
      }

      try {
        // Create NGL Stage - ensure the container has dimensions
        const container = viewerRef.current;
        if (container.offsetWidth === 0 || container.offsetHeight === 0) {
          console.warn('Container has no dimensions, waiting...');
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        const stage = new NGL.Stage(container, {
          backgroundColor: 'white',
        });
        stageRef.current = stage;

        // Handle resize
        const handleResize = () => {
          if (stage && mounted) {
            stage.handleResize();
          }
        };
        window.addEventListener('resize', handleResize);

        // Also use ResizeObserver to detect container size changes
        let resizeObserver: ResizeObserver | null = null;
        if (window.ResizeObserver) {
          resizeObserver = new ResizeObserver((entries) => {
            // Use requestAnimationFrame to avoid "ResizeObserver loop completed with undelivered notifications"
            requestAnimationFrame(() => {
              if (stage && mounted && entries.length > 0) {
                stage.handleResize();
              }
            });
          });
          resizeObserver.observe(container);
        }

        // Determine if we have a gene symbol or UniProt ID
        const uniprotPattern = /^[OPQ][0-9][A-Z0-9]{3}[0-9]$|^[A-NR-Z][0-9]([A-Z][A-Z0-9]{2}[0-9]){1,2}$/;
        let uniprotId = proteinId;

        if (!uniprotPattern.test(proteinId)) {
          // Try to resolve gene symbol to UniProt ID
          try {
            const response = await fetch(
              `https://rest.uniprot.org/uniprotkb/search?query=gene_exact:${proteinId}+AND+reviewed:true&fields=accession&size=1`
            );
            if (response.ok) {
              const data = await response.json();
              if (data.results && data.results.length > 0) {
                uniprotId = data.results[0].primaryAccession;
                console.log(`Resolved ${proteinId} to UniProt ID: ${uniprotId}`);
              }
            }
          } catch (e) {
            console.log('Could not resolve gene symbol');
          }
        }

        let structureLoaded = false;

        // Try to get PDB IDs from PDBe
        try {
          console.log(`Fetching PDB structures for UniProt ID: ${uniprotId}`);
          const pdbeResponse = await fetch(
            `https://www.ebi.ac.uk/pdbe/api/mappings/best_structures/${uniprotId}`
          );
          
          if (pdbeResponse.ok) {
            const pdbeData = await pdbeResponse.json();
            console.log('PDBe response:', pdbeData);
            const structures = pdbeData[uniprotId] || [];
            
            if (structures.length > 0) {
              // Get first unique PDB ID
              const pdbIdSet = new Set<string>(structures.map((s: any) => s.pdb_id));
              const pdbIds: string[] = Array.from(pdbIdSet);
              const firstPdbId: string = pdbIds[0];
              
              // Try to load the PDB file (try .pdb first, then .cif)
              let structure: any = null;
              const pdbIdUpper = firstPdbId.toUpperCase();
              
              // Try .pdb format first
              try {
                const pdbUrl = `https://files.rcsb.org/download/${pdbIdUpper}.pdb`;
                console.log(`Trying PDB format: ${pdbUrl}`);
                structure = await stage.loadFile(pdbUrl, { 
                  defaultRepresentation: false 
                });
                console.log('Successfully loaded .pdb file');
              } catch (e) {
                console.log('PDB format failed, trying CIF...');
                
                // Try .cif format as fallback
                try {
                  const cifUrl = `https://files.rcsb.org/download/${pdbIdUpper}.cif`;
                  console.log(`Trying CIF format: ${cifUrl}`);
                  structure = await stage.loadFile(cifUrl, { 
                    defaultRepresentation: false,
                    ext: 'cif'
                  });
                  console.log('Successfully loaded .cif file');
                } catch (cifError) {
                  console.log('Both PDB and CIF formats failed');
                  structure = null;
                }
              }
              
              if (structure && mounted) {
                structureRef.current = structure;
                
                // Add cartoon representation for the backbone
                structure.addRepresentation('cartoon', {
                  color: 'chainindex',
                  opacity: 0.9
                });
                
                // Add all cysteines with professional styling
                structure.addRepresentation('ball+stick', {
                  sele: 'CYS',
                  color: '#FFC107', // Material amber
                  scale: 1.5
                });
                
                // Don't highlight on initial load - let the highlight useEffect handle it
                structure.autoView();
                
                // Setup interaction handlers
                setupInteractionHandlers(stage);
                
                setStructureType('PDB');
                setPdbId(firstPdbId);
                structureLoaded = true;
                console.log(`PDB structure ${pdbIdUpper} loaded successfully`);
              }
            }
          }
        } catch (e) {
          console.log('PDBe API error:', e);
        }

        // If no PDB structure, try AlphaFold
        if (!structureLoaded && mounted) {
          try {
            const afUrl = `https://alphafold.ebi.ac.uk/files/AF-${uniprotId}-F1-model_v4.pdb`;
            console.log(`Loading AlphaFold structure: ${afUrl}`);
            
            const structure = await stage.loadFile(afUrl, { 
              defaultRepresentation: false 
            });
            
            if (mounted) {
              structureRef.current = structure;
              
              // Add cartoon with confidence coloring
              structure.addRepresentation('cartoon', {
                color: 'bfactor',
                colorScale: 'RdYlBu',
                colorReverse: true,
              });
              
              // Add all cysteines with professional styling
              structure.addRepresentation('ball+stick', {
                sele: 'CYS',
                color: '#FFC107', // Material amber
                scale: 1.5
              });
              
              // Don't highlight on initial load - let the highlight useEffect handle it
              structure.autoView();
              
              // Setup interaction handlers
              setupInteractionHandlers(stage);
              
              setStructureType('AlphaFold');
              structureLoaded = true;
              console.log('AlphaFold structure loaded successfully');
            }
          } catch (e) {
            console.log('Failed to load AlphaFold structure:', e);
          }
        }

        if (!structureLoaded) {
          throw new Error('Could not load any structure for this protein');
        }

        setIsLoading(false);

        // Cleanup
        return () => {
          mounted = false;
          window.removeEventListener('resize', handleResize);
          if (resizeObserver) {
            resizeObserver.disconnect();
          }
          if (stage) {
            stage.dispose();
          }
        };

      } catch (err: any) {
        console.error('Error in loadStructure:', err);
        if (mounted) {
          setError(err.message || 'Failed to load structure');
          setIsLoading(false);
        }
      }
    };

    // Use requestAnimationFrame to ensure browser has painted
    requestAnimationFrame(() => {
      if (mounted) {
        loadStructure();
      }
    });

    return () => {
      mounted = false;
      if (stageRef.current) {
        try {
          stageRef.current.dispose();
        } catch (e) {
          console.error('Cleanup error:', e);
        }
      }
    };
  }, [proteinId]); // ONLY reload when protein changes, NOT when clicking cysteines!

  // Reset chain selection when cysteine changes
  useEffect(() => {
    setCysteineWarning(null);
    setAvailableChains([]);
    setSelectedChain(null);
  }, [highlightCysteine]);

  // Handle highlight updates without reloading structure
  useEffect(() => {
    if (!structureRef.current || !stageRef.current || isLoading) return;

    try {
      // Remove all previous highlight representations
      const reps = structureRef.current.reprList.slice();
      reps.forEach((rep: any) => {
        if (rep.repr && rep.repr.parameters.color === '#FF1744') {
          structureRef.current.removeRepresentation(rep);
        }
      });

      // Add new highlight and zoom if specified
      if (highlightCysteine) {
        const resNum = parseInt(highlightCysteine.replace('C', ''));
        
        console.log(`Looking for cysteine at position ${resNum}...`);
        
        // Find all cysteines in the structure with their chains
        const cysteineInfo: any[] = [];
        const seenCysteines = new Set<string>();
        let matchingCys: any[] = [];
        
        try {
          // More comprehensive cysteine search
          structureRef.current.structure.eachAtom((atom: any) => {
            if (atom.resname === 'CYS') {
              const key = `${atom.chainname}:${atom.resno}`;
              if (!seenCysteines.has(key)) {
                seenCysteines.add(key);
                const cysInfo = {
                  resno: atom.resno,
                  chain: atom.chainname || 'Unknown',
                  chainIndex: atom.chainIndex,
                  full: key
                };
                cysteineInfo.push(cysInfo);
                if (atom.resno === resNum) {
                  matchingCys.push(cysInfo);
                }
              }
            }
          });
          
          console.log('All cysteines in structure:', cysteineInfo.sort((a, b) => {
            if (a.chain !== b.chain) return a.chain.localeCompare(b.chain);
            return a.resno - b.resno;
          }));
          console.log(`Looking for residue ${resNum}, found cysteines: ${cysteineInfo.map(c => c.full).join(', ')}`);
          
          if (matchingCys.length > 0) {
            console.log(`Found cysteine ${resNum} on chain(s): ${matchingCys.map(c => c.chain).join(', ')}`);
            const chains = matchingCys.map(c => c.chain).sort();
            // Set available chains after a small delay to avoid render loop
            setTimeout(() => {
              setAvailableChains(chains);
              if (!selectedChain && chains.length > 0) {
                setSelectedChain(chains[0]);
              }
            }, 0);
          }
        } catch (e) {
          console.error('Could not list cysteines:', e);
        }
        
        // Build selection patterns - start with generic patterns that work
        const selectionPatterns: string[] = [];
        
        // Always start with generic patterns that are known to work
        selectionPatterns.push(
          `${resNum} and CYS`,                              // This works on initial load
          `CYS and ${resNum}`,                              // Alternative order
        );
        
        // If we have matching cysteines, try chain-specific patterns
        if (matchingCys.length > 0 && selectedChain) {
          selectionPatterns.push(
            `:${selectedChain} and ${resNum} and CYS`,      // Chain-specific
            `${resNum}:${selectedChain} and CYS`,           // Compact notation
          );
        }
        
        // Generic fallback
        selectionPatterns.push(`${resNum}`);                // Just residue number
        
        let selectionString: string | null = null;
        let atomCount = 0;
        
        // Try each pattern until we find the cysteine
        for (const pattern of selectionPatterns) {
          try {
            const structure = structureRef.current.structure;
            const selection = structure.getSelection(pattern);
            if (selection && selection.atomCount > 0) {
              selectionString = pattern;
              atomCount = selection.atomCount;
              
              // Log which chain we found it on
              let foundChain = 'Unknown';
              structure.eachAtom((atom: any) => {
                if (atom.resno === resNum && atom.resname === 'CYS') {
                  foundChain = atom.chainname || 'Unknown';
                }
              }, selection);
              
              console.log(`Found cysteine ${resNum} on chain ${foundChain} with pattern: "${pattern}" (${atomCount} atoms)`);
              break;
            }
          } catch (e) {
            // Try next pattern
          }
        }
        
        if (!selectionString) {
          if (matchingCys.length > 0) {
            const warningMsg = `Could not select cysteine ${resNum} on chain ${selectedChain || 'any'}. Try selecting a different chain.`;
            console.warn(warningMsg);
            setCysteineWarning(warningMsg);
          } else {
            const warningMsg = `Cysteine at position ${resNum} not found in this ${structureType} structure`;
            console.warn(warningMsg);
            setCysteineWarning(warningMsg);
          }
          return;
        }
        
        // Cysteine found - clear any warning
        setCysteineWarning(null);
        
        // Add highlight representation with professional styling
        structureRef.current.addRepresentation('ball+stick', {
          sele: selectionString,
          color: '#FF1744', // Material red accent
          scale: 3,
        });
        
        // Zoom to the cysteine with proper selection
        setTimeout(() => {
          if (structureRef.current && stageRef.current) {
            try {
              structureRef.current.autoView(selectionString!, 1500);
              console.log(`Successfully zoomed to ${highlightCysteine}`);
            } catch (e) {
              console.error(`Zoom failed for ${highlightCysteine}:`, e);
            }
          }
        }, 50);
      }
    } catch (e) {
      console.error('Error updating highlight:', e);
    }
  }, [highlightCysteine, isLoading, structureType]); // Remove circular chain dependencies

  // Handle chain selection changes separately
  useEffect(() => {
    if (!structureRef.current || !stageRef.current || isLoading || !highlightCysteine || !selectedChain || availableChains.length <= 1) return;

    console.log(`Chain selection changed to: ${selectedChain}`);
    
    // Trigger re-highlight by updating a dummy state or directly manipulating the structure
    // Since we can't easily re-trigger the highlight useEffect, we'll handle it here
    try {
      // Remove all highlight representations
      const reps = structureRef.current.reprList.slice();
      reps.forEach((rep: any) => {
        if (rep.repr && rep.repr.parameters.color === '#FF1744') {
          structureRef.current.removeRepresentation(rep);
        }
      });

      const resNum = parseInt(highlightCysteine.replace('C', ''));
      
      // Try to find and highlight on specific chain
      const patterns = [
        `${resNum} and CYS`,                          // Generic first (always works)
        `:${selectedChain} and ${resNum} and CYS`,    // Chain-specific
        `${resNum}:${selectedChain} and CYS`,         // Compact
      ];
      
      let found = false;
      for (const pattern of patterns) {
        try {
          const selection = structureRef.current.structure.getSelection(pattern);
          if (selection && selection.atomCount > 0) {
            // For generic pattern, verify it includes the selected chain
            if (pattern === `${resNum} and CYS`) {
              let hasSelectedChain = false;
              structureRef.current.structure.eachAtom((atom: any) => {
                if (atom.chainname === selectedChain) {
                  hasSelectedChain = true;
                }
              }, selection);
              if (!hasSelectedChain && patterns.length > 1) {
                continue; // Try chain-specific patterns
              }
            }
            
            console.log(`Chain selection using pattern: ${pattern}`);
            structureRef.current.addRepresentation('ball+stick', {
              sele: pattern,
              color: '#FF1744',
              scale: 3,
            });
            
            // Zoom to it
            setTimeout(() => {
              if (structureRef.current) {
                structureRef.current.autoView(pattern, 1500);
              }
            }, 50);
            
            found = true;
            break;
          }
        } catch (e) {
          // Try next pattern
        }
      }
      
      if (!found) {
        console.warn(`Could not highlight cysteine ${resNum} on chain ${selectedChain}`);
      }
    } catch (e) {
      console.error('Error updating chain selection:', e);
    }
  }, [selectedChain]); // Only depend on selectedChain

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Typography variant="h6">
          {proteinId}
        </Typography>
        {structureType && !isLoading && (
          <>
            <Chip 
              label={structureType === 'PDB' ? `PDB: ${pdbId}` : 'AlphaFold'} 
              size="small"
              color={structureType === 'PDB' ? 'primary' : 'secondary'}
            />
            {pdbId && (
              <IconButton
                size="small"
                onClick={() => window.open(`https://www.rcsb.org/structure/${pdbId}`, '_blank')}
                title="View on RCSB PDB"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
      </Box>
      
      {/* Show chain selector if multiple chains available */}
      {availableChains.length > 1 && highlightCysteine && (
        <Box sx={{ px: 2, pt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Cysteine {highlightCysteine} found on chains:
          </Typography>
          {availableChains.map(chain => (
            <Chip
              key={chain}
              label={`Chain ${chain}`}
              size="small"
              variant={selectedChain === chain ? 'filled' : 'outlined'}
              color={selectedChain === chain ? 'primary' : 'default'}
              onClick={() => {
                console.log(`Switching to chain ${chain} for cysteine ${highlightCysteine}`);
                setSelectedChain(chain);
              }}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      )}
      
      {/* Show cysteine warning if any */}
      {cysteineWarning && (
        <Box sx={{ px: 2 }}>
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {cysteineWarning}
          </Alert>
        </Box>
      )}

      <Box sx={{ flex: 1, position: 'relative' }}>
        {isLoading && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading structure...</Typography>
          </Box>
        )}

        {error && !isLoading && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <div 
          ref={viewerRef} 
          style={{ 
            width: '100%', 
            height: '100%',
            display: (isLoading || error) ? 'none' : 'block'
          }} 
        />
      </Box>
      
      {/* Controls help */}
      {!isLoading && !error && (
        <Box sx={{ 
          position: 'absolute', 
          bottom: 10, 
          left: 10, 
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: 1,
          borderRadius: 1,
          fontSize: '12px',
          color: 'text.secondary'
        }}>
          <Typography variant="caption" display="block">
            🖱️ Left click + drag: Rotate | Middle/Wheel: Zoom | Right click + drag: Pan
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ProteinViewer;